/**
 * Farm Tour Agent
 * Plans and manages farm tours and visits
 */

interface FarmTourInfo {
  farmId: string;
  farmName: string;
  tourTypes: TourType[];
  availability: TourAvailability;
  amenities: string[];
  accessibility: AccessibilityInfo;
  policies: TourPolicies;
}

interface TourType {
  id: string;
  name: string;
  description: string;
  duration: number; // minutes
  price: number;
  minParticipants: number;
  maxParticipants: number;
  activities: string[];
  suitableFor: ('families' | 'adults' | 'schools' | 'seniors' | 'all')[];
  seasonalAvailability?: number[]; // months
}

interface TourAvailability {
  daysOpen: number[]; // 0-6, Sunday = 0
  hours: { open: string; close: string };
  blackoutDates: Date[];
  advanceBookingRequired: boolean;
  advanceBookingDays: number;
}

interface AccessibilityInfo {
  wheelchairAccessible: boolean;
  parkingAvailable: boolean;
  restroomAvailable: boolean;
  petFriendly: boolean;
  notes: string[];
}

interface TourPolicies {
  cancellationPolicy: string;
  weatherPolicy: string;
  groupDiscounts: { minSize: number; discount: number }[];
  whatToBring: string[];
  restrictions: string[];
}

interface TourRecommendation {
  farm: FarmTourInfo;
  tour: TourType;
  score: number;
  reasons: string[];
  bestTimeToVisit: string;
}

class FarmTourAgent {
  private defaultTourTypes: TourType[] = [
    {
      id: 'general-tour',
      name: 'General Farm Tour',
      description: 'A guided walk through the farm learning about our growing practices',
      duration: 60,
      price: 15,
      minParticipants: 2,
      maxParticipants: 15,
      activities: ['Farm walk', 'Meet the farmers', 'Sample produce'],
      suitableFor: ['all'],
    },
    {
      id: 'u-pick',
      name: 'U-Pick Experience',
      description: 'Pick your own fresh produce directly from our fields',
      duration: 90,
      price: 10,
      minParticipants: 1,
      maxParticipants: 30,
      activities: ['Picking instruction', 'Field access', 'Take home produce'],
      suitableFor: ['families', 'adults', 'all'],
      seasonalAvailability: [3, 4, 5, 6, 7, 8, 9, 10],
    },
    {
      id: 'educational',
      name: 'Educational Farm Visit',
      description: 'Learn about sustainable farming, composting, and food systems',
      duration: 120,
      price: 20,
      minParticipants: 10,
      maxParticipants: 40,
      activities: ['Classroom session', 'Hands-on activities', 'Q&A with farmers'],
      suitableFor: ['schools'],
    },
    {
      id: 'farm-to-table',
      name: 'Farm to Table Experience',
      description: 'Harvest ingredients and enjoy a meal prepared on the farm',
      duration: 180,
      price: 75,
      minParticipants: 8,
      maxParticipants: 20,
      activities: ['Harvest tour', 'Cooking demonstration', 'Farm fresh meal'],
      suitableFor: ['adults'],
    },
    {
      id: 'sunrise-tour',
      name: 'Sunrise Farm Tour',
      description: 'Experience the farm at dawn when produce is freshest',
      duration: 90,
      price: 25,
      minParticipants: 4,
      maxParticipants: 10,
      activities: ['Early morning harvest', 'Farm breakfast', 'Photography opportunities'],
      suitableFor: ['adults'],
    },
  ];

  /**
   * Get recommended tours based on preferences
   */
  getRecommendedTours(
    farms: FarmTourInfo[],
    preferences: {
      groupType: 'families' | 'adults' | 'schools' | 'seniors' | 'solo';
      groupSize: number;
      budget: number;
      interests: string[];
      date?: Date;
      duration?: number;
    }
  ): TourRecommendation[] {
    const recommendations: TourRecommendation[] = [];

    farms.forEach(farm => {
      farm.tourTypes.forEach(tour => {
        let score = 0;
        const reasons: string[] = [];

        // Check group type compatibility
        const groupMatch = tour.suitableFor.includes('all') ||
          (preferences.groupType === 'solo' && tour.minParticipants === 1) ||
          tour.suitableFor.includes(preferences.groupType as any);

        if (!groupMatch) return;

        score += 20;
        reasons.push(`Suitable for ${preferences.groupType}`);

        // Check group size
        if (
          preferences.groupSize >= tour.minParticipants &&
          preferences.groupSize <= tour.maxParticipants
        ) {
          score += 15;
          reasons.push('Perfect group size');
        }

        // Check budget
        if (tour.price <= preferences.budget) {
          score += 15;
          reasons.push('Within budget');
        }

        // Check interests
        preferences.interests.forEach(interest => {
          if (
            tour.activities.some(a => a.toLowerCase().includes(interest.toLowerCase())) ||
            tour.name.toLowerCase().includes(interest.toLowerCase())
          ) {
            score += 10;
            reasons.push(`Matches interest: ${interest}`);
          }
        });

        // Check seasonal availability
        if (preferences.date && tour.seasonalAvailability) {
          const month = preferences.date.getMonth() + 1;
          if (tour.seasonalAvailability.includes(month)) {
            score += 10;
            reasons.push('Available this season');
          }
        }

        // Check duration preference
        if (preferences.duration && Math.abs(tour.duration - preferences.duration) <= 30) {
          score += 10;
          reasons.push('Matches preferred duration');
        }

        if (score > 30) {
          recommendations.push({
            farm,
            tour,
            score,
            reasons,
            bestTimeToVisit: this.getBestTimeToVisit(farm, tour),
          });
        }
      });
    });

    return recommendations.sort((a, b) => b.score - a.score);
  }

  /**
   * Get best time to visit
   */
  private getBestTimeToVisit(farm: FarmTourInfo, tour: TourType): string {
    if (tour.name.includes('Sunrise')) return 'Early morning (6-8 AM)';
    if (tour.name.includes('U-Pick')) return 'Morning (8-10 AM) for freshest produce';
    if (tour.suitableFor.includes('schools')) return 'Weekday mornings';
    return 'Morning or early afternoon';
  }

  /**
   * Plan a multi-farm tour day
   */
  planTourDay(
    farms: FarmTourInfo[],
    userLocation: { lat: number; lng: number },
    maxFarms: number = 3
  ): {
    stops: { farm: FarmTourInfo; tour: TourType; suggestedTime: string }[];
    totalDuration: number;
    tips: string[];
  } {
    // Select diverse farms
    const selectedFarms = farms.slice(0, maxFarms);

    const stops = selectedFarms.map((farm, index) => {
      // Pick appropriate tour for each stop
      const tour = farm.tourTypes[0] || this.defaultTourTypes[0];

      const startHour = 9 + index * 2; // Space out by 2 hours
      const suggestedTime = `${startHour}:00 ${startHour < 12 ? 'AM' : 'PM'}`;

      return { farm, tour, suggestedTime };
    });

    const totalDuration = stops.reduce((sum, stop) => sum + stop.tour.duration + 30, 0); // +30 for travel

    const tips = [
      'Wear comfortable walking shoes',
      'Bring sunscreen and water',
      'Carry cash for farm stand purchases',
      'Check weather before departure',
      'Allow extra time between farms',
    ];

    return { stops, totalDuration, tips };
  }

  /**
   * Get seasonal tour calendar
   */
  getSeasonalCalendar(): { month: string; highlights: string[]; recommendedTours: string[] }[] {
    return [
      {
        month: 'January',
        highlights: ['Citrus season', 'Winter vegetables'],
        recommendedTours: ['General Farm Tour', 'Educational Farm Visit'],
      },
      {
        month: 'February',
        highlights: ['Strawberry season begins', 'Citrus peak'],
        recommendedTours: ['U-Pick Experience', 'Farm to Table Experience'],
      },
      {
        month: 'March',
        highlights: ['Spring planting', 'Early vegetables'],
        recommendedTours: ['Educational Farm Visit', 'General Farm Tour'],
      },
      {
        month: 'April',
        highlights: ['Strawberry peak', 'Spring flowers'],
        recommendedTours: ['U-Pick Experience', 'Sunrise Farm Tour'],
      },
      {
        month: 'May',
        highlights: ['Tomato season', 'Herb gardens'],
        recommendedTours: ['Farm to Table Experience', 'U-Pick Experience'],
      },
      {
        month: 'June',
        highlights: ['Summer produce abundance', 'Tropical fruits'],
        recommendedTours: ['U-Pick Experience', 'General Farm Tour'],
      },
      {
        month: 'July',
        highlights: ['Melons', 'Summer vegetables'],
        recommendedTours: ['Sunrise Farm Tour', 'U-Pick Experience'],
      },
      {
        month: 'August',
        highlights: ['Late summer harvest', 'Tropical fruits'],
        recommendedTours: ['U-Pick Experience', 'Farm to Table Experience'],
      },
      {
        month: 'September',
        highlights: ['Fall planting', 'Transition crops'],
        recommendedTours: ['Educational Farm Visit', 'General Farm Tour'],
      },
      {
        month: 'October',
        highlights: ['Pumpkins', 'Fall vegetables'],
        recommendedTours: ['U-Pick Experience', 'Educational Farm Visit'],
      },
      {
        month: 'November',
        highlights: ['Cool season crops', 'Thanksgiving produce'],
        recommendedTours: ['Farm to Table Experience', 'General Farm Tour'],
      },
      {
        month: 'December',
        highlights: ['Citrus begins', 'Holiday items'],
        recommendedTours: ['General Farm Tour', 'Farm to Table Experience'],
      },
    ];
  }

  /**
   * Generate packing list
   */
  generatePackingList(tour: TourType): string[] {
    const baseItems = [
      'Comfortable closed-toe shoes',
      'Weather-appropriate clothing',
      'Water bottle',
      'Sunscreen',
      'Hat or cap',
    ];

    const tourSpecificItems: { [key: string]: string[] } = {
      'u-pick': ['Containers for produce', 'Gardening gloves', 'Small cooler for transport'],
      'sunrise-tour': ['Light jacket', 'Camera', 'Insect repellent'],
      'farm-to-table': ['Appetite!', 'Allergy information', 'Optional wine pairing purchase'],
      'educational': ['Notebook', 'Questions prepared', 'Camera for documentation'],
    };

    const specificItems = tourSpecificItems[tour.id] || [];

    return [...baseItems, ...specificItems];
  }

  /**
   * Calculate group pricing
   */
  calculateGroupPricing(
    tour: TourType,
    groupSize: number,
    policies: TourPolicies
  ): { perPerson: number; total: number; savings: number; appliedDiscount: number } {
    let perPerson = tour.price;
    let appliedDiscount = 0;

    // Find applicable group discount
    const applicableDiscount = policies.groupDiscounts
      .filter(d => groupSize >= d.minSize)
      .sort((a, b) => b.minSize - a.minSize)[0];

    if (applicableDiscount) {
      appliedDiscount = applicableDiscount.discount;
      perPerson = tour.price * (1 - appliedDiscount / 100);
    }

    const total = perPerson * groupSize;
    const savings = tour.price * groupSize - total;

    return {
      perPerson: Math.round(perPerson * 100) / 100,
      total: Math.round(total * 100) / 100,
      savings: Math.round(savings * 100) / 100,
      appliedDiscount,
    };
  }

  /**
   * Get default tour types
   */
  getDefaultTourTypes(): TourType[] {
    return this.defaultTourTypes;
  }

  /**
   * Validate booking
   */
  validateBooking(
    tour: TourType,
    date: Date,
    groupSize: number,
    availability: TourAvailability
  ): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check day of week
    const dayOfWeek = date.getDay();
    if (!availability.daysOpen.includes(dayOfWeek)) {
      errors.push('Farm is closed on this day');
    }

    // Check blackout dates
    if (availability.blackoutDates.some(d => d.toDateString() === date.toDateString())) {
      errors.push('This date is not available for tours');
    }

    // Check group size
    if (groupSize < tour.minParticipants) {
      errors.push(`Minimum ${tour.minParticipants} participants required`);
    }
    if (groupSize > tour.maxParticipants) {
      errors.push(`Maximum ${tour.maxParticipants} participants allowed`);
    }

    // Check advance booking
    if (availability.advanceBookingRequired) {
      const daysUntil = Math.floor((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (daysUntil < availability.advanceBookingDays) {
        errors.push(`Booking required ${availability.advanceBookingDays} days in advance`);
      }
    }

    return { valid: errors.length === 0, errors };
  }
}

const farmTourAgent = new FarmTourAgent();
export default farmTourAgent;
